import { useEffect, useState } from 'react';
import { fetchAgentUsageStats } from '../services/dashboard.service';
import { useFilterContext } from '../usageandbilling/FilterContext';

export interface AgentUsageStats {
  totals: {
    promptTokens: number;
    completionTokens: number;
    totalTokens: number;
    calls: number;
    totalLatencyMs: number;
    avgLatencyMs: number;
  };
  tokensOverTime: Array<{ date: string; totalTokens: number; calls: number }>;
  byTool: Array<{ tool: string; calls: number; totalTokens: number }>;
  topUsers: Array<{ userId: string; userName: string; calls: number; totalTokens: number }>;
  sessions: { count: number };
}

export function useAgentUsageStats() {
  const { selectedOrgIds, selectedUserIds, dateRange } = useFilterContext();
  const [data, setData] = useState<AgentUsageStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const controller = new AbortController();

    const fetchStats = async () => {
      if (!dateRange[0] || !dateRange[1]) return;
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          startDate: dateRange[0].format('YYYY-MM-DD'),
          endDate: dateRange[1].format('YYYY-MM-DD'),
        });
        if (selectedOrgIds.length > 0) params.set('orgIds', selectedOrgIds.join(','));
        if (selectedUserIds.length > 0) params.set('userIds', selectedUserIds.join(','));

        const response = await fetchAgentUsageStats(params.toString(), controller.signal);
        setData(response as AgentUsageStats);
      } catch (err: any) {
        if (err?.name === 'AbortError') return;
        setError('Failed to load agent usage statistics.');
      } finally {
        setLoading(false);
      }
    };

    fetchStats();
    return () => controller.abort();
  }, [selectedOrgIds, selectedUserIds, dateRange]);

  return { data, loading, error };
}
